// Small shared pieces every Code workspace panel composes: section labels,
// the toolbar button, a popover menu, the search field, and a couple of
// formatting helpers. Styled entirely by code-editor.css.
import { useEffect, useRef, type ReactNode } from 'react';

import { CodeIcon, type CodeIconKind } from './codeIcons';
import './code-editor.css';

export function CwSectionLabel({ children, count, action }: { children: ReactNode; count?: number; action?: ReactNode }) {
  return (
    <div className="cw-section">
      <span className="cw-section-title">
        {children}
        {count !== undefined && <span className="cw-section-count">{count}</span>}
      </span>
      {action}
    </div>
  );
}

export interface CwToolButtonProps {
  icon?: CodeIconKind;
  label: string;
  /** Shown beside the icon; omit for an icon-only button (the label still
   * becomes the tooltip and accessible name). */
  showLabel?: boolean;
  shortcut?: string;
  title?: string;
  onClick?: () => void;
  active?: boolean;
  primary?: boolean;
  danger?: boolean;
  disabled?: boolean;
}

export function CwToolButton({ icon, label, showLabel = true, shortcut, title, onClick, active, primary, danger, disabled }: CwToolButtonProps) {
  const className = ['cw-tool', !showLabel && 'is-icon', active && 'is-active', primary && 'is-primary', danger && 'is-danger'].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      title={title ?? (shortcut ? `${label} (${shortcut})` : label)}
    >
      {icon && <CodeIcon kind={icon} />}
      {showLabel && <span>{label}</span>}
      {showLabel && shortcut && <span className="cw-tool-kbd">{shortcut}</span>}
    </button>
  );
}

export function CwMenu({
  open,
  onClose,
  align = 'right',
  width,
  children,
}: {
  open: boolean;
  onClose: () => void;
  align?: 'left' | 'right';
  width?: number;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div className="cw-menu-backdrop" onClick={onClose} />
      <div role="menu" className="cw-menu" style={{ top: '100%', marginTop: 4, [align]: 0, minWidth: width }}>
        {children}
      </div>
    </>
  );
}

export function CwMenuItem({
  icon,
  label,
  hint,
  shortcut,
  onClick,
  danger,
  disabled,
  checked,
}: {
  icon?: CodeIconKind;
  label: string;
  hint?: string;
  shortcut?: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Renders a checkmark state (for toggles) - `undefined` means "not a toggle". */
  checked?: boolean;
}) {
  return (
    <button
      type="button"
      role={checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
      aria-checked={checked}
      className={'cw-menu-item' + (danger ? ' is-danger' : '')}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="cw-menu-item-icon">{checked !== undefined ? checked ? <CodeIcon kind="check" size={13} /> : null : icon ? <CodeIcon kind={icon} size={14} /> : null}</span>
      <span className="cw-menu-item-body">
        <span>{label}</span>
        {hint && <span className="cw-menu-item-hint">{hint}</span>}
      </span>
      {shortcut && <span className="ss-kbd cw-menu-item-kbd" style={{ fontSize: 9.5, padding: '0 5px' }}>{shortcut}</span>}
    </button>
  );
}

export function CwMenuDivider() {
  return <div className="cw-menu-divider" role="separator" />;
}

export function CwMenuHeading({ children }: { children: ReactNode }) {
  return <div className="cw-menu-heading">{children}</div>;
}

export function CwSearchInput({
  value,
  onChange,
  placeholder,
  autoFocus,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoFocus?: boolean;
  ariaLabel?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <label className="cw-search">
      <CodeIcon kind="search" size={13} />
      <input
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        autoFocus={autoFocus}
        spellCheck={false}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && value) {
            e.stopPropagation();
            onChange('');
          }
        }}
      />
      {value && (
        <button type="button" onClick={() => { onChange(''); ref.current?.focus(); }} aria-label="Clear search">
          <CodeIcon kind="close" size={12} />
        </button>
      )}
    </label>
  );
}

/** `hh:mm:ss` in the viewer's locale-neutral 24h form, for the Output feed. */
export function formatClock(epochMs: number): string {
  const d = new Date(epochMs);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}`;
}

/** A script name as the editor shows it everywhere: `name` plus a dimmed
 * `.hws`, in one mono span. */
export function ScriptFileName({ name, className }: { name: string; className?: string }) {
  return (
    <span className={className}>
      {name}
      <span className="cw-ext">.hws</span>
    </span>
  );
}

/** Ctrl on Windows/Linux, ⌘ on a Mac - for shortcut hints in tooltips. */
export const MOD_KEY = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';
